/**
 * User Management — Admin UI for managing the user allowlist.
 *
 * Features:
 * - User table with role badges
 * - Invite user modal (creates Firestore record + IDP account for non-Google emails)
 * - Edit role/name modal
 * - Delete confirmation modal
 * - Self-protection (can't edit own role or delete self)
 */

import { useState, useEffect, useCallback } from 'react';
import { UserPlus, Pencil, Trash2, Copy, Check, ShieldAlert } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { authedFetch } from '../lib/api';
import { Button, Input, Select, Badge, Modal, Alert } from '@/components/ui';

interface UserRecord {
  email: string;
  display_name: string;
  role: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface InviteResult {
  email: string;
  auth_method: string;
  temp_password: string | null;
}

type ModalState =
  | { type: 'none' }
  | { type: 'invite' }
  | { type: 'invite-success'; result: InviteResult }
  | { type: 'edit'; user: UserRecord }
  | { type: 'delete'; user: UserRecord };

const ROLE_OPTIONS = ['admin', 'analyst', 'viewer'] as const;

function roleBadge(role: string) {
  switch (role) {
    case 'admin':
      return (
        <Badge variant="destructive" className="border border-destructive/40">
          {role}
        </Badge>
      );
    case 'analyst':
      return (
        <Badge variant="info" className="border border-info/40">
          {role}
        </Badge>
      );
    case 'viewer':
    default:
      return (
        <Badge variant="default" className="border border-gray-500/40">
          {role}
        </Badge>
      );
  }
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  // Form state for invite
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('viewer');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [detectedAuthMethod, setDetectedAuthMethod] = useState<string | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(false);

  // Debounced auth method detection via MX lookup
  useEffect(() => {
    const at = inviteEmail.indexOf('@');
    if (at < 1) { setDetectedAuthMethod(null); return; }
    const domain = inviteEmail.slice(at + 1).toLowerCase();
    if (!domain || !domain.includes('.')) { setDetectedAuthMethod(null); return; }

    setCheckingAuth(true);
    const timer = setTimeout(async () => {
      try {
        const res = await authedFetch(`/api/users/check-auth-method?email=${encodeURIComponent(inviteEmail)}`);
        const data = await res.json();
        setDetectedAuthMethod(data.auth_method);
      } catch {
        setDetectedAuthMethod(null);
      } finally {
        setCheckingAuth(false);
      }
    }, 500);

    return () => { clearTimeout(timer); setCheckingAuth(false); };
  }, [inviteEmail]);

  // Form state for edit
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<string>('viewer');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete state
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Copy button state
  const [copied, setCopied] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authedFetch('/api/users');
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data.users || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const isSelf = (email: string) => currentUser?.email?.toLowerCase() === email.toLowerCase();

  // ------ Invite ------
  const openInviteModal = () => {
    setInviteEmail('');
    setInviteName('');
    setInviteRole('viewer');
    setInviteError(null);
    setDetectedAuthMethod(null);
    setModal({ type: 'invite' });
  };

  const handleInvite = async () => {
    setInviteLoading(true);
    setInviteError(null);
    try {
      const res = await authedFetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail, display_name: inviteName, role: inviteRole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create user');
      setModal({ type: 'invite-success', result: data });
      fetchUsers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setInviteLoading(false);
    }
  };

  // ------ Edit ------
  const openEditModal = (u: UserRecord) => {
    setEditName(u.display_name);
    setEditRole(u.role);
    setEditError(null);
    setModal({ type: 'edit', user: u });
  };

  const handleEdit = async () => {
    if (modal.type !== 'edit') return;
    setEditLoading(true);
    setEditError(null);
    try {
      const res = await authedFetch(`/api/users/${encodeURIComponent(modal.user.email)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: editName, role: editRole }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update user');
      setModal({ type: 'none' });
      fetchUsers();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  // ------ Delete ------
  const handleDelete = async () => {
    if (modal.type !== 'delete') return;
    setDeleteLoading(true);
    try {
      const res = await authedFetch(`/api/users/${encodeURIComponent(modal.user.email)}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete user');
      }
      setModal({ type: 'none' });
      fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeModal = () => setModal({ type: 'none' });

  // ------ Render ------
  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">User Management</h1>
            <p className="text-muted-foreground mt-1">Manage who can access the Roamler Data Assistant</p>
          </div>
          <Button onClick={openInviteModal}>
            <UserPlus className="h-4 w-4" />
            Invite User
          </Button>
        </div>

        {/* Error banner */}
        {error && (
          <Alert variant="error" className="mb-6 flex items-center justify-between">
            <span>{error}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setError(null)}
              className="text-destructive hover:text-destructive/80 -mr-2"
            >
              <span className="sr-only">Dismiss</span>
              &times;
            </Button>
          </Alert>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        )}

        {/* Users table */}
        {!loading && (
          <div className="rounded-xl border border-primary/20 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-primary/5 border-b border-primary/20">
                  <th className="text-left px-6 py-3 text-sm font-medium text-muted-foreground">User</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-muted-foreground">Role</th>
                  <th className="text-left px-6 py-3 text-sm font-medium text-muted-foreground hidden md:table-cell">
                    Added By
                  </th>
                  <th className="text-right px-6 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground">
                      No users found. Invite someone to get started.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.email} className="border-b border-primary/10 hover:bg-primary/5 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center h-9 w-9 rounded-full bg-primary/20 border border-primary/40 text-sm font-semibold text-primary shrink-0">
                            {u.display_name
                              ? u.display_name
                                  .split(' ')
                                  .map((p) => p[0])
                                  .join('')
                                  .slice(0, 2)
                                  .toUpperCase()
                              : u.email.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{u.display_name || u.email}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{roleBadge(u.role)}</td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span className="text-sm text-muted-foreground">{u.created_by || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditModal(u)}
                            disabled={isSelf(u.email)}
                            aria-label={`Edit ${u.display_name || u.email}`}
                            title={isSelf(u.email) ? "Can't edit your own account" : 'Edit user'}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setModal({ type: 'delete', user: u })}
                            disabled={isSelf(u.email)}
                            aria-label={`Delete ${u.display_name || u.email}`}
                            title={isSelf(u.email) ? "Can't delete your own account" : 'Delete user'}
                            className="hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== MODALS ===== */}

      {/* Invite Modal */}
      <Modal open={modal.type === 'invite'} onClose={closeModal} title="Invite User">
        {inviteError && (
          <Alert variant="error" className="mb-4">
            {inviteError}
          </Alert>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
            <Input
              type="email"
              inputSize="sm"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
            />
            {checkingAuth && (
              <p className="text-xs mt-1.5 text-muted-foreground">Checking sign-in method...</p>
            )}
            {!checkingAuth && detectedAuthMethod && (
              <p className={`text-xs mt-1.5 ${detectedAuthMethod === 'google' ? 'text-blue-400' : 'text-amber-400'}`}>
                {detectedAuthMethod === 'google'
                  ? 'Google Workspace detected — will sign in with Google, no password needed'
                  : 'Will sign in with email & password — a temporary password will be generated'}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Display Name</label>
            <Input
              type="text"
              inputSize="sm"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Role</label>
            <Select selectSize="sm" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={closeModal}>
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={inviteLoading || !inviteEmail || !inviteName}>
            {inviteLoading ? 'Creating...' : 'Create User'}
          </Button>
        </div>
      </Modal>

      {/* Invite Success Modal */}
      <Modal open={modal.type === 'invite-success'} onClose={closeModal} title="User Created">
        {modal.type === 'invite-success' && (
          <>
            <div className="space-y-4">
              <Alert variant="success">{modal.result.email} has been added successfully.</Alert>

              <div>
                <p className="text-sm text-muted-foreground mb-1">Sign-in method</p>
                <p className="text-sm font-medium text-foreground">
                  {modal.result.auth_method === 'google' ? 'Google Sign-In' : 'Email & Password'}
                </p>
              </div>

              {modal.result.temp_password && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Temporary password</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 rounded-lg bg-background/80 border border-border text-sm font-mono text-foreground break-all">
                      {modal.result.temp_password}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(modal.result.temp_password!)}
                      title="Copy password"
                    >
                      {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Share this password securely with the user. They should change it after first login.
                  </p>
                </div>
              )}

              {modal.result.auth_method === 'google' && (
                <p className="text-xs text-muted-foreground">
                  The user can sign in directly with their Google account. No password needed.
                </p>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <Button onClick={closeModal}>Done</Button>
            </div>
          </>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal open={modal.type === 'edit'} onClose={closeModal} title="Edit User">
        {modal.type === 'edit' && (
          <>
            {editError && (
              <Alert variant="error" className="mb-4">
                {editError}
              </Alert>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                <Input
                  type="email"
                  inputSize="sm"
                  value={modal.user.email}
                  disabled
                  className="bg-background/30 text-muted-foreground cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Display Name</label>
                <Input type="text" inputSize="sm" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Role</label>
                <Select selectSize="sm" value={editRole} onChange={(e) => setEditRole(e.target.value)}>
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="ghost" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={handleEdit} disabled={editLoading || !editName}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={modal.type === 'delete'} onClose={closeModal}>
        {modal.type === 'delete' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-full bg-destructive/20">
                <ShieldAlert className="h-5 w-5 text-destructive" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">Remove User</h2>
            </div>

            <p className="text-sm text-muted-foreground mb-2">
              Are you sure you want to remove <span className="font-medium text-foreground">{modal.user.email}</span>?
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              They will lose all access to the Roamler Data Assistant. This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={closeModal}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Removing...' : 'Remove User'}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
